/**
 * A coordinator whose password was issued by a superadmin must replace it before doing anything else.
 *
 * The SERVER enforces this: every coordinator gate answers 403 `password_change_required` until the change
 * (backend middleware/temporaryPasswordGate.js). This module only routes the user to the one screen that works,
 * instead of letting them land on a dashboard whose every request is refused.
 *
 * Sign-in stores the flag from the session's `user.must_change_password`; the route guards in App.jsx read it;
 * the forced change screen clears it. It is in USER_SCOPED_KEYS (services/signInState.ts), so the next
 * account signed in on this browser does not inherit it.
 */
export const FORCED_CHANGE_PATH = '/change-password'
export const MUST_CHANGE_PASSWORD_KEY = 'mustChangePassword'

export function recordPasswordChangeRequired(required: boolean): void {
  try {
    if (required) globalThis.localStorage.setItem(MUST_CHANGE_PASSWORD_KEY, 'true')
    else globalThis.localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY)
  } catch {
    // storage unavailable — the server still refuses every request, so the user is not let through
  }
}

export function isPasswordChangeRequired(): boolean {
  try {
    return globalThis.localStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === 'true'
  } catch {
    return false
  }
}

/** Where a coordinator lands once they are signed in (and, after a forced change, once it is done). */
export function coordinatorHomePath(isSuperadmin: boolean): string {
  return isSuperadmin ? '/admin' : '/coordinator-dashboard'
}

/** Where a coordinator sign-in should land: the forced change screen first, if the password is temporary. */
export function landingPathAfterSignIn(user: { must_change_password?: boolean; is_superadmin?: boolean }): string {
  return user.must_change_password === true ? FORCED_CHANGE_PATH : coordinatorHomePath(user.is_superadmin === true)
}
